import React, { useEffect } from "react";
import { NavLink, Outlet, useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchPatient, clearCurrentPatient } from "../../../store/slices/patientsSlice";
import MainLayout from "../../../components/layout/MainLayout";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const ProfileLayout = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { currentPatient, isLoading, error } = useSelector((state) => state.patients);

    // Fetch patient when component mounts
    useEffect(() => {
        if (id) {
            dispatch(fetchPatient(id));
        }

        // Clear current patient on unmount
        return () => {
            dispatch(clearCurrentPatient());
        };
    }, [dispatch, id]);

    // useEffect(() => {
    //     if (window.location.pathname === `/patients/manage/${id}`) {
    //         navigate(`/patients/manage/${id}/enquiry-form`, { replace: true });
    //     }
    // }, [id, navigate]);

    useEffect(() => {
        if (window.location.pathname === `/patients/manage/${id}`) {
            navigate(`/patients/manage/${id}/cases`, { replace: true });
        }
    }, [id, navigate]);

    if (isLoading) {
        return (
            <MainLayout>
                <div className="container-xxl flex-grow-1 container-p-y">
                    <p>Loading patient details...</p>
                </div>
            </MainLayout>
        );
    }

    if (error) {
        return (
            <MainLayout>
                <div className="container-xxl flex-grow-1 container-p-y">
                    <p className="text-danger">{error}</p>
                </div>
            </MainLayout>
        );
    }

    if (!currentPatient) {
        return (
            <MainLayout>
                <div className="container-xxl flex-grow-1 container-p-y">
                    <p className="text-danger">Patient not found!</p>
                </div>
            </MainLayout>
        );
    }

    const patient = currentPatient;

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="row gy-6 gy-md-0">
                    {/* Sidebar */}
                    <div className="col-xl-4 col-lg-5 col-md-5 order-1 order-md-0">
                        <div className="card mb-6">
                            <div className="card-body pt-4">
                                <div className="user-avatar-section">
                                    <div className="d-flex align-items-center flex-column">
                                        <div
                                            className="rounded-3 mb-4 d-flex align-items-center justify-content-center bg-label-primary fw-bold"
                                            style={{ height: 120, width: 120, fontSize: 44, letterSpacing: 1 }}
                                            aria-label="Patient initials"
                                        >
                                            {`${(patient.first_name || "").trim().charAt(0)}${(patient.last_name || "").trim().charAt(0)}`.toUpperCase() || "?"}
                                        </div>
                                        <div className="user-info text-center">
                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                <h5 className="mb-0">
                                                    {patient.first_name} {patient.last_name}
                                                </h5>
                                                <span
                                                    className={`badge rounded-pill ${Number(patient.status) === 1
                                                        ? "bg-label-success"
                                                        : Number(patient.status) === 0
                                                            ? "bg-label-danger"
                                                            : "bg-label-warning"
                                                        }`}
                                                >
                                                    {Number(patient.status) === 1
                                                        ? "Active"
                                                        : Number(patient.status) === 0
                                                            ? "Inactive"
                                                            : "Pending"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <h5 className="pb-3 border-bottom mb-3 mt-4"></h5>
                                <ul className="list-unstyled mb-4">
                                    <li className="mb-2">
                                        <span className="fw-medium text-heading me-2">MR No:</span>
                                        <span className="fw-bold text-primary">{patient.mr_no || "N/A"}</span>
                                    </li>
                                    <li className="mb-2">
                                        <span className="fw-medium text-heading me-2">CNIC:</span>
                                        <span>{patient.cnic || "N/A"}</span>
                                    </li>
                                    <li className="mb-2">
                                        <span className="fw-medium text-heading me-2">Email:</span>
                                        <span>{patient.email || "N/A"}</span>
                                    </li>
                                    <li className="mb-2">
                                        <span className="fw-medium text-heading me-2">Phone:</span>
                                        <span>{patient.phone || "N/A"}</span>
                                    </li>
                                    <li className="mb-2">
                                        <span className="fw-medium text-heading me-2">Gender:</span>
                                        <span>{patient.gender}</span>
                                    </li>
                                    <li className="mb-2">
                                        <span className="fw-medium text-heading me-2">DOB:</span>
                                        <span>{patient.dob}</span>
                                    </li>
                                    <li className="mb-2">
                                        <span className="fw-medium text-heading me-2">Address:</span>
                                        <span>{patient.address || "N/A"}</span>
                                    </li>
                                    <li className="mb-2">
                                        <span className="fw-medium text-heading me-2">Emergency Contact:</span>
                                        <span>
                                            {patient.emergency_contact_name || "N/A"} (
                                            {patient.emergency_contact_phone || "N/A"})
                                        </span>
                                    </li>
                                    <li className="mb-2">
                                        <span className="fw-medium text-heading me-2">Created At:</span>
                                        <span>{new Date(patient.created_at).toLocaleDateString()}</span>
                                    </li>
                                </ul>

                                <div className="d-flex justify-content-center">
                                    <button
                                        className="btn btn-primary me-3"
                                        onClick={() => navigate(`/patients/edit/${id}`)}
                                    >
                                        Edit
                                    </button>
                                    {/* <button className="btn btn-outline-danger">Suspend</button> */}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="col-xl-8 col-lg-7 col-md-7 order-0 order-md-1">
                        <div className="nav-align-top">
                            <ul className="nav nav-pills flex-column flex-md-row mb-4">
                                <li className="nav-item">
                                    {/* <NavLink
                                        to={`/patients/manage/${id}/enquiry-form`}
                                        className={({ isActive }) =>
                                            `nav-link ${isActive ? "active" : ""}`
                                        }
                                    >
                                        <i className="ri-file-list-3-line me-2"></i> Enquiry Form
                                    </NavLink> */}
                                </li>
                                <li className="nav-item">
                                    <NavLink
                                        to={`/patients/manage/${id}/cases`}
                                        className={({ isActive }) =>
                                            `nav-link ${isActive ? "active" : ""}`
                                        }
                                    >
                                        <i className="menu-icon tf-icons ri-folder-user-line"></i> Cases
                                    </NavLink>
                                </li>
                            </ul>
                        </div>

                        <div className="card">
                            <div className="card-body">
                                <Outlet />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default ProfileLayout;
